/** Account-wide storage usage roll-up (overview stat cards + per-bucket rail). */
import { useQuery } from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type UsageOverview,
  type UsageSeries,
  type UsageSeriesPoint,
} from './_shared';

const EMPTY: UsageOverview = {
  buckets: 0,
  total_size: 0,
  total_objects: 0,
  by_bucket: [],
};

/**
 * Live usage overview (buckets, total bytes/objects, per-bucket aggregate).
 * The backend caps the per-bucket walk, so this can be momentarily stale on
 * very large accounts — refetched on a slow interval rather than realtime.
 */
export function useUsageOverview(serviceId: string | undefined) {
  return useQuery<UsageOverview>({
    queryKey: s3Keys.overview(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/overview`);
      const data = unwrap<UsageOverview>(res);
      return {
        ...EMPTY,
        ...data,
        by_bucket: data?.by_bucket ?? [],
      };
    },
  });
}

/**
 * Daily storage-utilization series for the overview chart. Only Wasabi-backed
 * services report history; other providers answer an empty series, which the
 * caller renders as an empty state rather than an error.
 */
export function useUsageSeries(serviceId: string | undefined) {
  return useQuery<UsageSeriesPoint[]>({
    queryKey: s3Keys.usageSeries(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/usage`);
      const data = unwrap<UsageSeries>(res);
      return data?.series ?? [];
    },
  });
}

/**
 * Daily storage-utilization series for a single bucket (bucket detail chart).
 * Same Wasabi-only contract as {@link useUsageSeries}: non-Wasabi services answer
 * an empty series, rendered as an empty state rather than an error.
 */
export function useBucketUsageSeries(
  serviceId: string | undefined,
  bucket: string | undefined,
) {
  return useQuery<UsageSeriesPoint[]>({
    queryKey: s3Keys.bucketUsage(serviceId ?? '', bucket ?? ''),
    enabled: !!serviceId && !!bucket,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${storageBase(serviceId!)}/buckets/${bucket!}/utilization`,
      );
      const data = unwrap<UsageSeries>(res);
      return data?.series ?? [];
    },
  });
}
